import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, HStack, Text, Tooltip } from '@enechain/ecloud-designsystem'
import { FaFileRegular, FaTrashCanRegular } from '@enechain/ecloud-icons'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { iconSize } from '~/constants/iconSize'
import { isEnterKey } from '~/libs/keyboard'

type Props = {
  label: string
  memo: string
  onEdit: () => void
  onDelete: () => Promise<void>
  date?: string
  editAriaLabel?: string
  isPending?: boolean
  editable?: boolean
  deletable?: boolean
  /**
   * 幅を design のコンパクト表示 (`max-width: 480px`) に収めるか。既定 false。
   *
   * design はサブステップメモと承認メモで**別の見せ方**をしている:
   *
   * - サブステップメモ `.substep-memo-display` (`case-detail.html:390-400`)
   *   … `min-width: min(240px,100%)` + `max-width: 480px` + 1 行省略の
   *     コンパクトなピル。全文はクリックで dialog。
   * - 承認メモ `.substep-approval-record` (同 :374-378)
   *   … 左罫線付きの**ブロック**。`white-space: pre-line` で折り返して全文を出し、
   *     幅上限も省略も持たない。
   *
   * 共有 component に上限を無条件で当てると承認メモまで 480px に押し込まれるため
   * (2026-09-07 レビュー指摘)、要否は呼び出し側が明示する。
   */
  compact?: boolean
}

/**
 * メモを常時表示するバッジ。左から 📄 アイコン、記入者ラベル、本文、
 * 削除アイコンを一行で配置する (AC-WFW-005b/007a/008b)。
 * バッジ本体クリックで編集、削除アイコンで確認後削除。
 */
export const MemoBadge = ({
  label,
  memo,
  onEdit,
  onDelete,
  date,
  editAriaLabel,
  isPending = false,
  editable = true,
  deletable = true,
  compact = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  return (
    <>
      <HStack align="center" gap={1}>
        {/* tooltip 本文は改行を保持する (spec AC-WFW-005b「複数行 (改行可)」,
            slice #7328 / #7054, D5)。バッジ本体は 1 行 + 省略のまま
            (whiteSpace="nowrap" below) — 全文表示は tooltip の役目。
            本文に style (`pre-line`) を付けるため string 専用の `HintTooltip`
            ではなく DS `Tooltip` を直接使い、空判定もここで持つ (前回承認メモは
            空文字 note を保持しうる: `sub_step_previous_approval_note.go`)。 */}
        <Tooltip
          content={
            <Text whiteSpace="pre-line" wordBreak="break-word">
              {memo}
            </Text>
          }
          isHidden={memo === ''}
        >
          <Box
            alignItems="center"
            gap={1}
            display="flex"
            minWidth="min(240px, 100%)"
            /*
             * 長いメモでパネルが横に伸びるのを止める上限 (2026-09-04 デザイナー指摘)。
             *
             * design `demand/case-detail.html:390-400` の `.substep-memo-display` は
             * `min-width: min(240px, 100%)` と **`max-width: 480px`** を対で持ち、
             * 「コンパクト幅で右端に配置 (伸ばさない)」と明記している。実装は
             * `min-width` だけ写して `max-width` を `100%` にしていたため、
             * メモが長いと行いっぱいに広がっていた。
             *
             * 上限は `compact` を渡した呼び出し側だけに当てる。承認メモは design で
             * 幅上限を持たない別表現のため (prop の JSDoc 参照)。
             *
             * 文字数制限は足さない。design は**溢れる前提**で「1 行 + `…` 省略、
             * 全文はクリックで dialog」という読ませ方にしている (本文の
             * `textOverflow` と tooltip が対応する)。
             */
            maxWidth={compact ? '480px' : '100%'}
            padding="6px 10px"
            textAlign="left"
            background="Surface.primary"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="base"
            cursor={editable ? 'pointer' : undefined}
            aria-label={editAriaLabel}
            onClick={editable ? onEdit : undefined}
            onKeyDown={
              editable
                ? (e) => {
                    if (isEnterKey(e) || e.key === ' ') {
                      e.preventDefault()
                      onEdit()
                    }
                  }
                : undefined
            }
            role={editable ? 'button' : undefined}
            tabIndex={editable ? 0 : undefined}
            title={memo}
          >
            <FaFileRegular
              boxSize={iconSize.guideButton}
              color="Text.midEmphasis"
            />
            <Text flexShrink={0} color="Text.midEmphasis" fontSize="sm">
              {label}
            </Text>
            <Text
              overflow="hidden"
              color="Text.highEmphasis"
              fontSize="sm"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {memo}
            </Text>
            {date !== undefined && (
              <Text flexShrink={0} color="Text.lowEmphasis" fontSize="xs">
                {date}
              </Text>
            )}
          </Box>
        </Tooltip>
        {/* 削除アイコンは**永続性で使い分ける** (design `specs/tech-spec.md`
            §「除去 / 削除アイコンは永続性で使い分け」): 未保存・ドラフトの
            除去は × (確認なし)、 DB 登録済レコードの削除はゴミ箱 (+確認
            ダイアログ)。 このメモは保存済みレコードで、 押すと確認ダイアログを
            出してから API で消すため**ゴミ箱**が正 (2026-09-03 デザイナー指摘
            「X かゴミ箱かはルールに合わせる」)。 同じ画面の案件削除
            (`CaseActionBar`) も既に FaTrashCanRegular。

            8080 の case-detail はここが × だが、 プロト側のメモは
            `setMeta` の画面内 state で**確認ダイアログも無い** (= プロトでは
            ドラフト除去に相当する) ため、 上の規約とは矛盾しない。

            variant は `ghost`。 `outline` は DS button recipe の base が
            色なしショートハンド `border: 'none'` を outline variant の
            `border-style: solid` より前に出すため、 枠が medium (3px) +
            currentColor に化けて**濃い黒の四角**になる。 8080 も
            `.substep-memo-delete` は `btn-tertiary` (枠なし) で、
            リポジトリ内の IconButton も ghost が最多。 */}
        {deletable && (
          <IconOnlyButton
            label={t('page:workflowCaseDetail.memoBadge.deleteAriaLabel')}
            onClick={() => {
              setIsDeleteConfirmOpen(true)
            }}
            size="xs"
            title={t('page:workflowCaseDetail.memoBadge.deleteAriaLabel')}
            variant="ghost"
          >
            <FaTrashCanRegular />
          </IconOnlyButton>
        )}
      </HStack>
      {isDeleteConfirmOpen && (
        <ConfirmDialog
          body={t('page:workflowCaseDetail.memoBadge.deleteConfirmBody')}
          confirmLabel={t(
            'page:workflowCaseDetail.memoBadge.deleteConfirmLabel',
          )}
          destructive
          isLoading={isPending}
          isOpen={isDeleteConfirmOpen}
          onCancel={() => {
            setIsDeleteConfirmOpen(false)
          }}
          onConfirm={() => {
            void onDelete().finally(() => {
              setIsDeleteConfirmOpen(false)
            })
          }}
          title={t('page:workflowCaseDetail.memoBadge.deleteConfirmTitle')}
        />
      )}
    </>
  )
}
